import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import toast from 'react-hot-toast'
import Card, { CardHeader } from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Spinner from '../../../components/ui/Spinner'
import api from '../../../lib/api'
import type { Settings } from '../../../types'

// API response interface (snake_case)
interface LabProfileResponse {
  id?: string
  name?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  state_code?: string
  pincode?: string
  gstin?: string
  pan?: string
  phone?: string
  email?: string
  bank_name?: string
  account_number?: string
  ifsc_code?: string
  account_holder_name?: string
  account_type?: string
  category_code?: string
  upi_id?: string
  upi_qr_url?: string
}

// Form data interface (camelCase)
interface LabProfileForm {
  name: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  stateCode: string
  pincode: string
  gstin: string
  pan: string
  phone: string
  email: string
  bankName: string
  accountNumber: string
  ifscCode: string
  accountHolderName: string
  accountType: string
  categoryCode: string
  upiId: string
  upiQrUrl: string
}

type SettingsTab = 'profile' | 'print' | 'gst' | 'notifications'

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  const tabs: { id: SettingsTab; label: string; icon: string }[] = [
    { id: 'profile', label: 'Lab Profile', icon: 'fa-building' },
    { id: 'print', label: 'Print Header', icon: 'fa-print' },
    { id: 'gst', label: 'GST Settings', icon: 'fa-receipt' },
    { id: 'notifications', label: 'Notifications', icon: 'fa-bell' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your lab configuration and preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <Card padding="sm">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${activeTab === tab.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                  <i className={`fas ${tab.icon} w-5`}></i>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && <LabProfileSettings />}
          {activeTab === 'print' && <PrintHeaderSettings />}
          {activeTab === 'gst' && <GSTSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
        </div>
      </div>
    </div>
  )
}

function LabProfileSettings() {
  const queryClient = useQueryClient()
  const [formInitialized, setFormInitialized] = useState(false)

  const { data: profile, isLoading, isFetched } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      try {
        const { data } = await api.get<{ data: LabProfileResponse | null }>('/settings/lab-profile')
        return data?.data || null
      } catch (err) {
        // Return null if the endpoint fails (e.g., no profile exists yet)
        console.error('Failed to fetch lab profile:', err)
        return null
      }
    },
    retry: false,
    staleTime: 0, // Always fetch fresh data for settings
  })

  const [formData, setFormData] = useState<LabProfileForm>({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    stateCode: '',
    pincode: '',
    gstin: '',
    pan: '',
    phone: '',
    email: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    accountType: '',
    categoryCode: '',
    upiId: '',
    upiQrUrl: '',
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Initialize form when profile data loads - only once after first successful fetch
  useEffect(() => {
    if (isFetched && !formInitialized) {
      if (profile) {
        setFormData({
          name: profile.name || '',
          addressLine1: profile.address_line1 || '',
          addressLine2: profile.address_line2 || '',
          city: profile.city || '',
          state: profile.state || '',
          stateCode: profile.state_code || '',
          pincode: profile.pincode || '',
          gstin: profile.gstin || '',
          pan: profile.pan || '',
          phone: profile.phone || '',
          email: profile.email || '',
          bankName: profile.bank_name || '',
          accountNumber: profile.account_number || '',
          ifscCode: profile.ifsc_code || '',
          accountHolderName: profile.account_holder_name || '',
          accountType: profile.account_type || '',
          categoryCode: profile.category_code || '',
          upiId: profile.upi_id || '',
          upiQrUrl: profile.upi_qr_url || '',
        })
      }
      setFormInitialized(true)
    }
  }, [profile, isFetched, formInitialized])

  const mutation = useMutation({
    mutationFn: async (data: LabProfileForm) => {
      // Convert camelCase to snake_case for API
      return api.put('/settings/lab-profile', {
        name: data.name,
        address_line1: data.addressLine1,
        address_line2: data.addressLine2 || null,
        city: data.city,
        state: data.state,
        state_code: data.stateCode,
        pincode: data.pincode,
        gstin: data.gstin,
        pan: data.pan || null,
        phone: data.phone,
        email: data.email,
        bank_name: data.bankName || null,
        account_number: data.accountNumber || null,
        ifsc_code: data.ifscCode || null,
        account_holder_name: data.accountHolderName || null,
        account_type: data.accountType || null,
        category_code: data.categoryCode || null,
        upi_id: data.upiId || null,
        upi_qr_url: data.upiQrUrl || null,
      })
    },
    onSuccess: () => {
      setFormInitialized(false) // Reset so form repopulates with fresh data after save
      queryClient.invalidateQueries({ queryKey: ['settings', 'lab-profile'] })
      toast.success('Lab profile saved successfully')
    },
    onError: () => {
      toast.error('Failed to save lab profile')
    },
  })
  
  // Generate UPI payment URL for QR code
  const generateUPIUrl = (upiId: string, name: string) => {
    if (!upiId) return ''
    const encodedName = encodeURIComponent(name || 'Payment')
    return `upi://pay?pa=${upiId}&pn=${encodedName}&cu=INR`
  }
  
  // Handle QR code image upload
  const handleQRUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // Convert to base64 for now (in production, upload to cloud storage)
    const reader = new FileReader()
    reader.onloadend = () => {
      setFormData({ ...formData, upiQrUrl: reader.result as string })
    }
    reader.readAsDataURL(file)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader title="Lab Profile" subtitle="Your business information appears on invoices" />
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Lab Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Address Line 1"
            value={formData.addressLine1}
            onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
            required
          />
        </div>

        <Input
          label="Address Line 2"
          value={formData.addressLine2}
          onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Input
            label="City"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            required
          />
          <Input
            label="State"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            required
          />
          <Input
            label="State Code"
            value={formData.stateCode}
            onChange={(e) => setFormData({ ...formData, stateCode: e.target.value })}
            required
          />
          <Input
            label="Pincode"
            value={formData.pincode}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="GST Number"
            value={formData.gstin}
            onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
            placeholder="22AAAAA0000A1Z5"
            helperText="Optional - Enter if registered for GST"
          />
          <Input
            label="PAN Number"
            value={formData.pan}
            onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
            placeholder="AAAAA0000A"
            helperText="Optional"
          />
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        <h4 className="font-medium text-gray-900 dark:text-white">
          Bank Details (Optional)
          <span className="text-sm font-normal text-gray-500 ml-2">- for invoices</span>
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="Bank Name"
            value={formData.bankName}
            onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
            placeholder="e.g., HDFC Bank"
          />
          <Input
            label="Account Number"
            value={formData.accountNumber}
            onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
            placeholder="e.g., 12345678901234"
          />
          <Input
            label="IFSC Code"
            value={formData.ifscCode}
            onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
            placeholder="e.g., HDFC0001234"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="Account Holder Name"
            value={formData.accountHolderName}
            onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value.replace(/[^a-zA-Z\s]/g, '') })}
            placeholder="Enter Account Holder Name"
            helperText="Optional - Alphabets and spaces only"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Account Type
            </label>
            <select
              value={formData.accountType}
              onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select Account Type</option>
              <option value="CURRENT">Current A/C</option>
              <option value="SAVINGS">Savings A/C</option>
            </select>
            {formData.accountNumber && !formData.accountType && (
              <p className="mt-1 text-xs text-amber-600">Required when account number is provided</p>
            )}
          </div>
          <Input
            label="Category Code"
            value={formData.categoryCode}
            onChange={(e) => setFormData({ ...formData, categoryCode: e.target.value })}
            placeholder="Enter Category Code"
            helperText="Optional - Alphanumeric"
          />
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        <h4 className="font-medium text-gray-900 dark:text-white">
          UPI Payment (Optional)
          <span className="text-sm font-normal text-gray-500 ml-2">- for invoices</span>
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="UPI ID"
              value={formData.upiId}
              onChange={(e) => setFormData({ ...formData, upiId: e.target.value })}
              placeholder="yourname@upi or 1234567890@ybl"
              helperText="Enter your UPI ID (e.g., name@paytm, name@okicici)"
            />
            
            {formData.upiId && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Auto-generated QR Code from UPI ID:
                </p>
                <div className="flex justify-center bg-white p-4 rounded-lg w-fit mx-auto">
                  <QRCodeSVG 
                    value={generateUPIUrl(formData.upiId, formData.name)} 
                    size={150}
                    level="M"
                    includeMargin={true}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 text-center">
                  Scan to pay via any UPI app
                </p>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Or Upload Custom QR Code
              </label>
              <p className="text-xs text-gray-500 mb-2">
                If you have a specific QR code image from your bank/UPI app
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleQRUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-upload mr-2"></i>
                Upload QR Image
              </Button>
            </div>
            
            {formData.upiQrUrl && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Uploaded QR Code:
                </p>
                <div className="flex justify-center bg-white p-4 rounded-lg w-fit mx-auto">
                  <img 
                    src={formData.upiQrUrl} 
                    alt="UPI QR Code" 
                    className="max-w-[150px] max-h-[150px] object-contain"
                  />
                </div>
                <div className="flex justify-center mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, upiQrUrl: '' })}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    <i className="fas fa-trash mr-1"></i>
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <i className="fas fa-info-circle mr-2"></i>
            <strong>Note:</strong> If you provide a UPI ID, a QR code will be auto-generated. You can also upload a custom QR code image if you prefer to use one from your bank or UPI app. The uploaded QR code will take priority on invoices.
          </p>
        </div>

        <div className="flex justify-end">
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Save Changes
          </Button>
        </div>

        {mutation.isSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">
              <i className="fas fa-check-circle mr-2"></i>
              Profile updated successfully!
            </p>
          </div>
        )}
      </form>
    </Card>
  )
}

// Default Print Header content
const DEFAULT_PRINT_HEADER = `{{LabName}}
Dr. N Sivasangkar B.D.S || Dr. S Nithya B.D.S
No: 130 Kovai Road, Karur, 639002 Phone: 9344104051`

function PrintHeaderSettings() {
  const queryClient = useQueryClient()
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Fetch lab profile to get lab name for preview
  const { data: labProfile } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      try {
        const { data } = await api.get<{ data: LabProfileResponse | null }>('/settings/lab-profile')
        return data?.data || null
      } catch {
        return null
      }
    },
  })

  // Fetch all settings including print header
  const { data: settings, isLoading, isFetched } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: async () => {
      try {
        const { data } = await api.get<{ data: Settings[] }>('/settings')
        return data?.data || []
      } catch (err) {
        console.error('Failed to fetch settings:', err)
        return []
      }
    },
    retry: false,
    staleTime: 0,
  })

  const [printHeader, setPrintHeader] = useState(DEFAULT_PRINT_HEADER)
  const [formInitialized, setFormInitialized] = useState(false)

  // Initialize from fetched settings
  useEffect(() => {
    if (isFetched && !formInitialized && settings) {
      const headerSetting = settings.find((s: Settings) => s.key === 'print_header_text')
      if (headerSetting) {
        setPrintHeader(headerSetting.value)
      }
      setFormInitialized(true)
    }
  }, [settings, isFetched, formInitialized])

  const mutation = useMutation({
    mutationFn: async (headerText: string) => {
      return api.put('/settings/bulk', {
        settings: [{ key: 'print_header_text', value: headerText }]
      })
    },
    onSuccess: () => {
      setFormInitialized(false)
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setSaveSuccess(true)
      toast.success('Print header saved successfully')
      setTimeout(() => setSaveSuccess(false), 3000)
    },
    onError: () => {
      toast.error('Failed to save print header')
    },
  })

  const handleSave = () => {
    mutation.mutate(printHeader)
  }

  const handleReset = () => {
    setPrintHeader(DEFAULT_PRINT_HEADER)
  }

  // Generate preview by replacing {{LabName}} with actual lab name
  const getPreviewText = () => {
    const labName = labProfile?.name || 'Lab Name'
    return printHeader.replace(/\{\{LabName\}\}/g, labName)
  }

  // Get preview lines for rendering
  const previewLines = getPreviewText().split('\n')

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader
        title="Print Header"
        subtitle="Configure the header that appears on all PDFs (Work Orders, Invoices, Statements)"
      />
      <div className="space-y-6">
        {/* Instructions */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
            <i className="fas fa-info-circle mr-2"></i>
            How it works
          </h4>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li><i className="fas fa-check-circle mr-2"></i>Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{LabName}}'}</code> to auto-insert your lab name (from Lab Profile)</li>
            <li><i className="fas fa-check-circle mr-2"></i>Line 1 is rendered <strong>bold</strong> in PDFs</li>
            <li><i className="fas fa-check-circle mr-2"></i>Each line break creates a new line in the header</li>
          </ul>
        </div>

        {/* Textarea for header */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Header Text
          </label>
          <textarea
            value={printHeader}
            onChange={(e) => setPrintHeader(e.target.value)}
            rows={5}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none font-mono text-sm"
            placeholder="Enter your print header text..."
          />
          <p className="mt-1 text-xs text-gray-500">
            Tip: Keep it concise (3-4 lines max) to leave space for document content
          </p>
        </div>

        {/* Preview */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <i className="fas fa-eye mr-2"></i>
            Preview (as it will appear in PDFs)
          </label>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              {previewLines.map((line, index) => (
                <p
                  key={index}
                  className={index === 0 ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}
                  style={{ fontSize: index === 0 ? '14px' : '12px' }}
                >
                  {line || '\u00A0'}
                </p>
              ))}
            </div>
          </div>
        </div>

        {saveSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">
              <i className="fas fa-check-circle mr-2"></i>
              Print header saved successfully!
            </p>
          </div>
        )}

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Failed to save. Please try again.
            </p>
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="secondary" onClick={handleReset}>
            <i className="fas fa-undo mr-2"></i>
            Reset to Default
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Save Header
          </Button>
        </div>
      </div>
    </Card>
  )
}

function GSTSettings() {
  const queryClient = useQueryClient()
  const [saveSuccess, setSaveSuccess] = useState(false)

  const { data: settings, isLoading, isFetched: gstFetched } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: async () => {
      try {
        const { data } = await api.get<{ data: Settings[] }>('/settings')
        return data?.data || []
      } catch (err) {
        console.error('Failed to fetch settings:', err)
        return []
      }
    },
    retry: false,
    staleTime: 0,
  })

  const [gstPercent, setGstPercent] = useState('0') // Default 0% for healthcare
  const [defaultSac, setDefaultSac] = useState('999312') // SAC code for dental services

  const [gstInitialized, setGstInitialized] = useState(false)

  // Initialize from fetched settings - only once after first successful fetch
  useEffect(() => {
    if (gstFetched && !gstInitialized && settings) {
      const gstSetting = settings.find((s: Settings) => s.key === 'gst_default_percent')
      const sacSetting = settings.find((s: Settings) => s.key === 'gst_default_sac')
      if (gstSetting) setGstPercent(gstSetting.value)
      if (sacSetting) setDefaultSac(sacSetting.value)
      setGstInitialized(true)
    }
  }, [settings, gstFetched, gstInitialized])

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Card>
    )
  }

  const mutation = useMutation({
    mutationFn: async (settingsData: { key: string; value: string }[]) => {
      return api.put('/settings/bulk', { settings: settingsData })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setSaveSuccess(true)
      toast.success('GST settings saved successfully')
      setTimeout(() => setSaveSuccess(false), 3000)
    },
    onError: () => {
      toast.error('Failed to save GST settings')
    },
  })

  const handleSave = () => {
    mutation.mutate([
      { key: 'gst_default_percent', value: gstPercent },
      { key: 'gst_default_sac', value: defaultSac },
    ])
  }

  return (
    <Card>
      <CardHeader title="GST Settings" subtitle="Configure default GST rates and SAC codes for dental services" />
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SearchableSelect
            label="Default GST Rate"
            value={gstPercent}
            onChange={setGstPercent}
            searchable={false}
            helperText="Applied to new catalog items (dental services typically 0%)"
            options={[
              { value: '0', label: '0% (Healthcare Exempt)' },
              { value: '5', label: '5%' },
              { value: '12', label: '12%' },
              { value: '18', label: '18%' },
              { value: '28', label: '28%' },
            ]}
          />
          <SearchableSelect
            label="Default SAC Code"
            value={defaultSac}
            onChange={setDefaultSac}
            searchable={false}
            helperText="Service Accounting Code for dental lab services"
            options={[
              { value: '999312', label: '999312 - Dental services (0% GST)' },
              { value: '998599', label: '998599 - Other professional services' },
            ]}
          />
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">GST/SAC Information</h4>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li><i className="fas fa-check-circle mr-2"></i><strong>0% GST:</strong> Dental and medical products/services are currently exempt from GST</li>
            <li><i className="fas fa-check-circle mr-2"></i><strong>999312:</strong> SAC code for dental services</li>
            <li><i className="fas fa-info-circle mr-2"></i>GST fields are maintained for future compliance if regulations change</li>
          </ul>
        </div>

        {saveSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">
              <i className="fas fa-check-circle mr-2"></i>
              GST settings saved successfully!
            </p>
          </div>
        )}

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Failed to save settings. Please try again.
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Save Settings
          </Button>
        </div>
      </div>
    </Card>
  )
}

function NotificationSettings() {
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [smsNotifications, setSmsNotifications] = useState(true)
  const [orderUpdates, setOrderUpdates] = useState(true)
  const [paymentAlerts, setPaymentAlerts] = useState(true)
  const [overdueReminders, setOverdueReminders] = useState(true)

  return (
    <Card>
      <CardHeader title="Notification Settings" subtitle="Configure how you receive updates" />
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
              <p className="text-sm text-gray-500">Receive updates via email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">SMS Notifications</p>
              <p className="text-sm text-gray-500">Receive updates via SMS</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={smsNotifications}
                onChange={(e) => setSmsNotifications(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>

        <h4 className="font-medium text-gray-900 dark:text-white pt-4">Notification Types</h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Order Updates</p>
              <p className="text-sm text-gray-500">Status changes on work orders</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={orderUpdates}
                onChange={(e) => setOrderUpdates(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Payment Alerts</p>
              <p className="text-sm text-gray-500">New payments received</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={paymentAlerts}
                onChange={(e) => setPaymentAlerts(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Overdue Reminders</p>
              <p className="text-sm text-gray-500">Alerts for overdue invoices</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={overdueReminders}
                onChange={(e) => setOverdueReminders(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button>
            <i className="fas fa-save mr-2"></i>
            Save Preferences
          </Button>
        </div>
      </div>
    </Card>
  )
}
